import { FC, Fragment, useContext, useEffect, useState } from "react";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { cx } from "shared/utils";
import { CriminalReport } from "types/landlord/reports";
import { ChevronDown } from "untitledui-js-base";
import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import { Section } from "views/applications-id-report/components/Section";
import { DynamicKeyValue } from "views/applications-id-report/components/SmartRender/DynamicKeyValue";
import { PrintAllContext } from "views/applications-id-report/providers/PrintProvider";
import { formatDate } from "views/applications-id-report/utils";
import { OffensesTable } from "./OffensesTable";

type GroupedCriminalRecordsProps = {
  title: string;
  verificationId: number;
  total: number;
  results: CriminalReport["results"];
};

export const GroupedCriminalRecords: FC<GroupedCriminalRecordsProps> = ({
  title,
  results,
  total,
  verificationId
}) => {
  const { documentPrintingState } = useContext(PrintAllContext);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (documentPrintingState?.length) setOpen(true);
  }, [documentPrintingState]);

  if (!results.length) return null;

  return (
    <Section
      icon={
        <span
          className={cx(
            "rounded px-1 text-white my-0 py-0.5 h-4 min-w-4 font-bold text-xxs flex items-center justify-center",
            total > 0 ? "bg-danger-500" : "bg-success-500"
          )}
        >
          {total}
        </span>
      }
      sectionClassName="md:p-1"
      key={`report-${verificationId}}`}
      sectionTitle={
        <p className="text-base font-medium text-content-1000">{title}</p>
      }
      maxWidth="none"
      rightElement={
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.SM}
          onClick={() => setOpen((prev) => !prev)}
        >
          <div className="flex items-center gap-2">
            <ChevronDown
              className={cx(
                "stroke-content-300",
                open ? "rotate-180 transition-all" : ""
              )}
              size="20"
            />
            {open ? "Hide" : "Show"}
          </div>
        </Button>
      }
    >
      <Show if={open}>
        {results.map((result, resultIndex) => {
          const { subject, case_details, offenses } = result;

          return (
            <Fragment key={`report-${verificationId}-${resultIndex}`}>
              <div className="p-2">
                <ContentTitle title={`Record ${resultIndex + 1}`}>
                  <div className="mb-6 flex gap-4">
                    <img
                      src={
                        subject.image_url ||
                        "/assets/criminal_report_image_placeholder.svg"
                      }
                      alt="subject image"
                      className="w-40 rounded object-contain"
                    />

                    <div>
                      <p className="text-sm font-medium text-content-300">
                        Disclaimer
                      </p>
                      <span className="mt-2.5 text-sm font-medium text-content-1000">
                        {subject.disclaimers || "N/A"}
                      </span>
                    </div>
                  </div>

                  <ContentTitle title="Name">
                    <DynamicKeyValue
                      detailsKey="First name"
                      value={subject.first_name}
                    />

                    <DynamicKeyValue
                      detailsKey="Last name"
                      value={subject.last_name}
                    />

                    <DynamicKeyValue
                      detailsKey="Middle name"
                      value={subject.middle_name}
                    />
                  </ContentTitle>

                  <ContentTitle title="Subject details">
                    <DynamicKeyValue
                      detailsKey="Date of birth"
                      value={formatDate(subject.dob)}
                    />

                    <DynamicKeyValue
                      detailsKey="Ethnicity"
                      value={subject.ethnicity}
                    />

                    <DynamicKeyValue
                      detailsKey="Eye color"
                      value={subject.eye_color}
                    />

                    <DynamicKeyValue detailsKey="Gender" value={subject.sex} />

                    <DynamicKeyValue
                      detailsKey="Hair color"
                      value={subject.hair_color}
                    />

                    <DynamicKeyValue
                      detailsKey="Height"
                      value={subject.height}
                    />

                    <DynamicKeyValue detailsKey="Race" value={subject.race} />

                    <DynamicKeyValue
                      detailsKey="Identify mark"
                      value={subject.scar_marks}
                    />
                  </ContentTitle>

                  <ContentTitle title="Address">
                    <DynamicKeyValue
                      detailsKey="City"
                      value={subject.primary_address?.city}
                    />

                    <DynamicKeyValue
                      detailsKey="State"
                      value={subject.primary_address?.state}
                    />

                    <DynamicKeyValue
                      detailsKey="Street name"
                      value={subject.primary_address?.street_name}
                    />

                    <DynamicKeyValue
                      detailsKey="Street number"
                      value={subject.primary_address?.street_number}
                    />

                    <DynamicKeyValue
                      detailsKey="Zip code"
                      value={subject.primary_address?.zip}
                    />
                  </ContentTitle>
                </ContentTitle>

                <ContentTitle title="Case details" contentClassName="pt-4 pb-0">
                  <DynamicKeyValue
                    detailsKey="Case docker number"
                    value={case_details.case_docket_number}
                  />

                  <DynamicKeyValue
                    detailsKey="Case file date"
                    value={formatDate(case_details.case_file_date)}
                  />

                  <DynamicKeyValue
                    detailsKey="Case number"
                    value={case_details.case_number}
                  />

                  <DynamicKeyValue
                    detailsKey="Case type"
                    value={case_details.case_type}
                  />

                  <DynamicKeyValue
                    detailsKey="Category"
                    value={case_details.category}
                  />

                  <DynamicKeyValue
                    detailsKey="Court jurisdiction"
                    value={case_details.jurisdiction}
                  />

                  <DynamicKeyValue
                    detailsKey="Court name"
                    value={case_details.court_name}
                  />

                  <DynamicKeyValue
                    detailsKey="Court type"
                    value={case_details.court_type}
                  />

                  <DynamicKeyValue
                    detailsKey="State"
                    value={case_details.state}
                  />

                  <DynamicKeyValue
                    detailsKey="Comments"
                    value={case_details.comments}
                  />
                </ContentTitle>
              </div>

              <OffensesTable offenses={offenses} />
            </Fragment>
          );
        })}
      </Show>
    </Section>
  );
};
